import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { useCurrency } from "../contexts/CurrencyContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Gift, Zap, Lock, Sparkles, Trophy } from "lucide-react";
import { playSpinStart, playSpinTick, playVictory, playGiftChime } from "../lib/sound";

// Display prizes matching backend FREE_GIFT_WHEEL (must stay in sync)
const PRIZES = [
  { value: 0.50, color: "#1A3050", label: "$0.50" },
  { value: 1.00, color: "#2A4570", label: "$1.00" },
  { value: 2.00, color: "#00B8D4", label: "$2.00" },
  { value: 3.00, color: "#00E676", label: "$3.00" },
  { value: 4.00, color: "#FF9500", label: "$4.00" },
  { value: 5.00, color: "#D4AF37", label: "$5.00" },
];

const SEG_COUNT = PRIZES.length;
const SEG_ANGLE = 360 / SEG_COUNT;

// Build CSS conic-gradient from prize colors
const conicBg = `conic-gradient(${PRIZES.map((p, i) => `${p.color} ${i * SEG_ANGLE}deg ${(i + 1) * SEG_ANGLE}deg`).join(", ")})`;

export default function FreeRoulette() {
  const { user, refreshUser } = useAuth();
  const { t } = useLang();
  const { fmt } = useCurrency();
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [prize, setPrize] = useState(null);
  const [tickers, setTickers] = useState([]);

  const ads = user?.ads_watched_total || 0;
  const used = user?.free_roulette_used || 0;
  const needed = 1000 * (used + 1);
  const available = ads >= needed;

  useEffect(() => () => tickers.forEach(clearTimeout), [tickers]);

  const spin = async () => {
    if (!available || spinning) return;
    setSpinning(true);
    setPrize(null);
    playSpinStart();
    try {
      const { data } = await api.post("/roulette/free");
      const wonValue = data.prize;
      const idx = PRIZES.findIndex((p) => Math.abs(p.value - wonValue) < 0.01);
      const safeIdx = idx >= 0 ? idx : 0;
      // Land prize at the top pointer
      const target = 360 * 7 + (360 - (safeIdx * SEG_ANGLE + SEG_ANGLE / 2));
      setRotation((r) => r + target);

      // Decelerating click ticks (~5s)
      const newTickers = [];
      let elapsed = 0;
      const base = 70;
      const schedule = (delay) => {
        const id = setTimeout(() => {
          playSpinTick(1400 - Math.min(1100, elapsed / 4));
        }, delay);
        newTickers.push(id);
      };
      while (elapsed < 4500) {
        schedule(elapsed);
        const factor = 1 + (elapsed / 5000) * 6;
        elapsed += base * factor;
      }
      setTickers(newTickers);

      setTimeout(async () => {
        setSpinning(false);
        setPrize(wonValue);
        playVictory();
        playGiftChime();
        toast.success(`🎁 +${fmt(wonValue)}`);
        await refreshUser();
        // Confetti
        const colors = wonValue >= 3 ? ["#D4AF37", "#FFD700", "#00E676", "#00E5FF"] : ["#00E5FF", "#00E676"];
        const burst = (origin) => confetti({
          particleCount: 100, spread: 90, startVelocity: 50, origin, colors,
        });
        burst({ x: 0.5, y: 0.5 });
        setTimeout(() => burst({ x: 0.2, y: 0.7 }), 200);
        setTimeout(() => burst({ x: 0.8, y: 0.7 }), 400);
      }, 5000);
    } catch (err) {
      setSpinning(false);
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-center">
      <h1 className="text-3xl sm:text-4xl font-bold text-cyan-gradient mb-2 flex items-center justify-center gap-3">
        <Gift className="w-7 h-7" /> {t("freeRoulette")}
      </h1>
      <p className="text-sm text-[#8FA3BD] mb-2">
        🎁 الجوائز من $0.50 إلى $5.00 (احتمالات تنازلية — الجائزة الأكبر = الأندر)
      </p>
      <p className="text-xs text-[#8FA3BD] mb-8 flex items-center justify-center gap-1.5">
        <Zap className="w-3 h-3 text-[#FF9500]" />{ads.toLocaleString()} / {needed.toLocaleString()} {t("adsWatched")}
      </p>

      {/* Probability legend */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mb-6 text-[10px]">
        {[
          { v: "$0.50", p: "50%" },
          { v: "$1.00", p: "25%" },
          { v: "$2.00", p: "15%" },
          { v: "$3.00", p: "7%" },
          { v: "$4.00", p: "2.5%" },
          { v: "$5.00", p: "0.5%" },
        ].map((x, i) => (
          <div key={i} className="bento-card p-2" data-testid={`prize-prob-${i}`}>
            <div className="font-bold text-cyan-gradient">{x.v}</div>
            <div className="text-[#8FA3BD]">{x.p}</div>
          </div>
        ))}
      </div>

      <div className="bento-card p-6 sm:p-10 flex flex-col items-center relative overflow-hidden">
        {/* Spinning glow background */}
        {spinning && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[#D4AF37]/10 blur-3xl animate-pulse" />
          </div>
        )}

        <div className="relative w-72 h-72 sm:w-96 sm:h-96">
          <div className="roulette-pointer" data-testid="gift-pointer" />
          <div
            className="w-full h-full rounded-full"
            style={{
              background: conicBg,
              transform: `rotate(${rotation}deg)`,
              transition: "transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)",
              boxShadow: "inset 0 0 0 4px #1A2D4D, inset 0 0 0 8px #D4AF37, 0 30px 60px rgba(0,0,0,0.5), 0 0 60px rgba(212,175,55,0.3)",
            }}
            data-testid="gift-wheel-rotor"
          >
            {/* Prize labels on each segment */}
            {PRIZES.map((p, i) => {
              const angle = i * SEG_ANGLE + SEG_ANGLE / 2;
              return (
                <div key={i}
                  className="absolute left-1/2 top-1/2 origin-left text-xs sm:text-sm font-bold pointer-events-none"
                  style={{
                    transform: `rotate(${angle}deg) translate(50px, -50%)`,
                    color: "#fff",
                    textShadow: "0 1px 4px rgba(0,0,0,0.9)",
                    width: "90px", whiteSpace: "nowrap",
                  }}>
                  {p.label}
                </div>
              );
            })}
            {/* Center hub */}
            <div className="absolute inset-1/4 rounded-full bg-[#050E1A] border-4 border-[#D4AF37] flex items-center justify-center">
              {spinning ? (
                <Sparkles className="w-10 h-10 text-[#D4AF37] animate-spin" />
              ) : prize !== null ? (
                <div>
                  <Trophy className="w-7 h-7 text-[#D4AF37] mx-auto mb-1" />
                  <div className="text-[10px] text-[#8FA3BD]">مكافأة الولاء</div>
                  <div className="text-3xl text-cyan-gradient font-bold" data-testid="gift-prize-display">{fmt(prize)}</div>
                </div>
              ) : (
                <div>
                  <Gift className="w-8 h-8 text-[#D4AF37] mx-auto mb-1" />
                  <div className="text-base text-cyan-gradient font-bold">هدية مجانية</div>
                  <div className="text-[10px] text-[#8FA3BD] mt-0.5">Free Gift</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Button onClick={spin} disabled={!available || spinning} className="btn-cyan mt-8 px-10" size="lg" data-testid="free-spin-btn">
          {!available ? <><Lock className="w-4 h-4 me-2" /> {needed - ads} {t("adsNeeded")}</> : spinning ? "..." : t("spinFree")}
        </Button>
        {!available && (
          <div className="text-xs text-[#8FA3BD] mt-3">
            {typeof t("rouletteLockedDesc") === "function" ? t("rouletteLockedDesc")(needed - ads) : `شاهد ${needed - ads} إعلان إضافي`}
          </div>
        )}
        {available && !spinning && prize === null && (
          <div className="text-xs text-[#00E676] mt-3 animate-pulse">
            ✨ الهدية جاهزة! اضغط الزر للسحب
          </div>
        )}
      </div>
    </div>
  );
}
